import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Search, X } from 'lucide-react'

import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import {
  TECTO_ENTIDADES,
  indexarModelo,
} from '@core/layout/search/buildIndex'
import {
  type EntradaPesquisa,
  type ResultadoPesquisa,
  descrever,
  procurar,
} from '@core/layout/search/searchIndex'

/** Pesquisa global: chegar a um elemento por GlobalId, nome ou classe.
 *
 *  Até aqui, para encontrar um elemento era preciso saber onde ele
 *  estava na árvore ou montar um filtro. Quem recebe um GlobalId num
 *  relatório de IDS, num tópico BCF ou num email não tinha por onde
 *  começar.
 *
 *  O índice é construído à primeira procura e não ao carregar o
 *  modelo: quem nunca procura nada não paga a leitura de centenas de
 *  milhares de nomes. Depois de construído fica em memória até os
 *  modelos mudarem. */
export default function GlobalSearch() {
  const models = useAppStore((s) => s.models)
  const setSelection = useAppStore((s) => s.setSelection)

  const [aberta, setAberta] = useState(false)
  const [consulta, setConsulta] = useState('')
  // O índice guarda consigo a assinatura dos modelos com que foi
  // construído. Assim, quando os modelos mudam, ele fica velho e
  // ignora-se na leitura, em vez de ser preciso um efeito a limpá-lo
  // (que obrigava a um render extra e a um setState dentro de efeito).
  const [indice, setIndice] = useState<{
    assinatura: string
    entradas: EntradaPesquisa[]
    parcial: boolean
  }>({ assinatura: '', entradas: [], parcial: false })
  const [aIndexar, setAIndexar] = useState(false)
  const [destacado, setDestacado] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const indexadoPara = useRef<string>('')
  const cancelar = useRef(false)

  // A assinatura dos modelos carregados. Quando muda, o índice que
  // havia deixou de valer.
  const assinatura = useMemo(() => models.map((m) => m.id).join('|'), [models])

  // Atalho: "/" abre a pesquisa, como no IFClite e em tantos outros.
  // Só quando o foco não está já num campo de texto, senão escrever
  // uma barra numa propriedade abria isto por cima.
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return
      const alvo = e.target as HTMLElement | null
      const aEscrever =
        alvo?.tagName === 'INPUT' ||
        alvo?.tagName === 'TEXTAREA' ||
        alvo?.isContentEditable
      if (aEscrever) return
      e.preventDefault()
      setAberta(true)
      window.setTimeout(() => inputRef.current?.focus(), 0)
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [])

  const valido = indice.assinatura === assinatura
  // Memoizado para a lista nao ser um array novo a cada render, o que
  // faria o useMemo da procura correr sempre.
  const entradas = useMemo(
    () => (valido ? indice.entradas : []),
    [valido, indice.entradas],
  )
  const parcial = valido && indice.parcial

  const garantirIndice = async () => {
    if (indexadoPara.current === assinatura || aIndexar) return
    indexadoPara.current = assinatura
    cancelar.current = false
    setAIndexar(true)
    const acumulado: EntradaPesquisa[] = []
    let algumParcial = false
    for (const m of models) {
      const modelo = viewerApi.getModel(m.id) as unknown as Parameters<
        typeof indexarModelo
      >[1] | null
      if (!modelo) continue
      try {
        const r = await indexarModelo(
          m.id,
          modelo,
          (lote) => {
            acumulado.push(...lote)
            // Mostrar resultados enquanto o resto ainda está a ser
            // lido: num modelo grande, esperar pelo fim é esperar
            // demasiado.
            setIndice({
              assinatura,
              entradas: [...acumulado],
              parcial: false,
            })
          },
          () => cancelar.current,
          TECTO_ENTIDADES,
        )
        algumParcial = algumParcial || r.parcial
      } catch {
        // Um modelo que não deixe indexar não impede os outros.
      }
    }
    setIndice({ assinatura, entradas: acumulado, parcial: algumParcial })
    setAIndexar(false)
  }

  const resultados = useMemo(
    () => procurar(entradas, consulta),
    [entradas, consulta],
  )

  const escolher = (r: ResultadoPesquisa) => {
    setSelection({ modelId: r.modelId, localId: r.localId })
    void viewerApi.focusOnSelection()
    setAberta(false)
    setConsulta('')
  }

  const fechar = () => {
    cancelar.current = true
    setAberta(false)
    setConsulta('')
  }

  if (!aberta) {
    return (
      <button
        type="button"
        data-tour="pesquisa-global"
        onClick={() => {
          setAberta(true)
          window.setTimeout(() => inputRef.current?.focus(), 0)
        }}
        title="Procurar por GlobalId, nome ou classe IFC  ( / )"
        className="flex items-center gap-2 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
      >
        <Search className="h-3.5 w-3.5" aria-hidden />
        <span className="hidden sm:inline">Procurar</span>
        <kbd className="hidden rounded border border-[var(--color-border)] px-1 text-[10px] sm:inline">
          /
        </kbd>
      </button>
    )
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded border border-[var(--color-accent-border)] bg-[var(--color-surface)] px-2 py-1">
        {aIndexar ? (
          <Loader2
            className="h-3.5 w-3.5 animate-spin text-[var(--color-accent)]"
            aria-hidden
          />
        ) : (
          <Search className="h-3.5 w-3.5 text-[var(--color-text-dim)]" aria-hidden />
        )}
        <input
          ref={inputRef}
          value={consulta}
          onFocus={() => void garantirIndice()}
          onChange={(e) => {
            setConsulta(e.target.value)
            // Voltar ao primeiro resultado aqui, e nao num efeito:
            // escrever e' o acontecimento que muda a lista.
            setDestacado(0)
            void garantirIndice()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              fechar()
              return
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setDestacado((i) => Math.min(i + 1, resultados.length - 1))
              return
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault()
              setDestacado((i) => Math.max(i - 1, 0))
              return
            }
            if (e.key === 'Enter' && resultados[destacado]) {
              e.preventDefault()
              escolher(resultados[destacado])
            }
          }}
          placeholder="GlobalId, nome ou classe IFC…"
          aria-label="Procurar no modelo"
          className="w-56 bg-transparent text-xs text-[var(--color-text)] outline-none placeholder:text-[var(--color-text-dim)]"
        />
        <button
          type="button"
          onClick={fechar}
          aria-label="Fechar a pesquisa"
          className="text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>

      {consulta.length >= 2 && (
        <div className="absolute right-0 z-[900] mt-1 max-h-80 w-96 overflow-y-auto rounded border border-[var(--color-border)] bg-[var(--color-panel)] shadow-xl">
          {resultados.length === 0 ? (
            <p className="px-3 py-2 text-xs text-[var(--color-text-dim)]">
              {aIndexar
                ? 'A ler o modelo…'
                : models.length === 0
                  ? 'Não há nenhum modelo carregado.'
                  : 'Nada encontrado.'}
            </p>
          ) : (
            <ul>
              {resultados.map((r, i) => (
                <li key={`${r.modelId}:${r.localId}`}>
                  <button
                    type="button"
                    onMouseEnter={() => setDestacado(i)}
                    onClick={() => escolher(r)}
                    className={`flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-xs ${
                      i === destacado
                        ? 'bg-[var(--color-accent-bg)] text-[var(--color-text)]'
                        : 'text-[var(--color-text-dim)] hover:bg-[var(--color-surface)]'
                    }`}
                  >
                    <span className="min-w-0 flex-1 truncate">{descrever(r)}</span>
                    {r.motivo === 'guid' && (
                      <span className="shrink-0 text-[10px] text-[var(--color-accent)]">
                        GlobalId
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {parcial && (
            <p className="border-t border-[var(--color-border)] px-3 py-1.5 text-[10px] text-[var(--color-warn-text)]">
              Modelo grande: a pesquisa parou nas primeiras{' '}
              {TECTO_ENTIDADES.toLocaleString('pt-PT')} entidades. Para procurar
              em tudo, usa o Filtro.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
